/**
 * TasksPage — top-level page for the Task 2.0 feature.
 *
 * Composes:
 *   - View tabs (List / Kanban / Gantt / Calendar)
 *   - Top filter bar (status / assignee / priority / due window) — applied
 *     to whichever data source the active view consumes.
 *   - "+ New Task" CTA opens a Modal Dialog
 *   - TaskDetailPanel (slide-in)
 *   - Owns local state for the visible task collection
 *
 * Light theme only. Linear / Notion-style cleanness.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  CalendarDays,
  CheckSquare,
  GanttChartSquare,
  KanbanSquare,
  ListChecks,
  Plus,
  RefreshCw,
} from "lucide-react";

import { TaskApi, taskApi as defaultApi } from "./api";
import { TaskBoard } from "./TaskBoard";
import { TaskCalendar } from "./TaskCalendar";
import { queryClient } from "@/lib/queryClient";
import { TaskDetailPanel } from "./TaskDetailPanel";
import { TaskGantt } from "./TaskGantt";
import { TaskList } from "./TaskList";
import type { TaskAccess } from "./access";
import {
  PRIORITY_LABELS_JA,
  STATUS_LABELS_JA,
  TASK_PRIORITIES,
  TASK_STATUSES,
} from "./types";
import type {
  CreateTaskInput,
  GanttView,
  KanbanColumn,
  KanbanView,
  Task,
  TaskDependency,
  TaskPriority,
  TaskStatus,
  UpdateTaskInput,
} from "./types";

export type TasksView = "list" | "board" | "gantt" | "calendar";

export interface TasksPageProps {
  /** Optional: pin the page to a single room (tasks are filtered server-side). */
  roomId?: string;
  /** Optional: pin the page to a single project. */
  projectId?: string;
  /** Defaults to the singleton client; tests can inject a mock. */
  api?: TaskApi;
  /** Initial view (default: list). */
  initialView?: TasksView;
  members?: { id: string; displayName: string }[];
  /** Task to open in the detail panel on arrival (e.g. /tasks?task=<id> from an assignment notification). */
  initialTaskId?: string;
  access?: TaskAccess;
  onRefreshAccess?: () => Promise<unknown>;
}

const VIEW_META: Record<TasksView, { label: string; icon: typeof ListChecks }> = {
  list: { label: "リスト", icon: ListChecks },
  board: { label: "カンバン", icon: KanbanSquare },
  gantt: { label: "ガント", icon: GanttChartSquare },
  calendar: { label: "カレンダー", icon: CalendarDays },
};

type DueBucket = "any" | "overdue" | "today" | "week" | "month" | "none";

interface Filters {
  statuses: Set<TaskStatus>;
  assignee: string;
  priority: TaskPriority | "all";
  due: DueBucket;
}

const INITIAL_FILTERS: Filters = {
  statuses: new Set<TaskStatus>(),
  assignee: "",
  priority: "all",
  due: "any",
};

export function TasksPage({
  roomId,
  projectId,
  api = defaultApi,
  initialView = "list",
  members = [],
  initialTaskId,
  access,
  onRefreshAccess,
}: TasksPageProps) {
  const [view, setView] = useState<TasksView>(initialView);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [board, setBoard] = useState<KanbanView | null>(null);
  const [gantt, setGantt] = useState<GanttView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Task | null>(null);
  const selectedId = useRef<string | null>(null);
  const selectedRequest = useRef(0);
  const [selectedDeps, setSelectedDeps] = useState<{
    blocked_by: TaskDependency[];
    blocks: TaskDependency[];
  }>({ blocked_by: [], blocks: [] });
  const [selectedSubs, setSelectedSubs] = useState<Task[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const canCreate = access?.can_create === true;
  const canAssign = access?.can_assign === true;
  const canEdit = (task: Task) => access?.can_manage_tasks === true || (access?.can_edit_own === true && task.reporter === access.user_id);
  const canUpdateStatus = (task: Task) => canEdit(task) || (access?.can_update_assigned === true && task.assignee === access.user_id);

  const closeSelection = useCallback(() => {
    selectedId.current = null;
    selectedRequest.current += 1;
    setSelected(null);
    setSelectedDeps({ blocked_by: [], blocks: [] });
    setSelectedSubs([]);
  }, []);

  useEffect(() => {
    closeSelection();
    return () => {
      selectedId.current = null;
      selectedRequest.current += 1;
    };
  }, [closeSelection, access?.role, access?.workspace_id, access?.user_id]);

  useEffect(() => {
    if (!canCreate) setShowCreate(false);
  }, [canCreate]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // List + Calendar share the flat list endpoint.
      if (view === "list" || view === "calendar") {
        const r = await api.listTasks({ room_id: roomId, project_id: projectId });
        setTasks(r.tasks);
      } else if (view === "board") {
        const v = await api.kanban({ room_id: roomId, project_id: projectId });
        setBoard(v);
        // Mirror the columns into `tasks` so filters can compute counts.
        setTasks(v.columns.flatMap((c) => c.tasks));
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const v = await api.gantt({ room_id: roomId, project_id: projectId, from: today.getTime() });
        setGantt(v);
        setTasks(v.bars.map((b) => b.task));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [api, view, roomId, projectId, access?.role, access?.workspace_id, access?.user_id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const refreshSelected = useCallback(
    async (id: string) => {
      if (selectedId.current !== id) return;
      const request = ++selectedRequest.current;
      try {
        const [info, subs] = await Promise.all([
          api.getTask(id),
          api.listSubtasks(id),
        ]);
        if (selectedId.current !== id || selectedRequest.current !== request) return;
        setSelected(info.task);
        setSelectedDeps(info.dependencies);
        setSelectedSubs(subs.subtasks);
      } catch (e) {
        if (selectedId.current !== id || selectedRequest.current !== request) return;
        if (e instanceof Error && /not_found/.test(e.message)) {
          closeSelection();
        }
      }
    },
    [api, closeSelection],
  );

  const handleSelect = useCallback(
    (task: Task) => {
      selectedId.current = task.id;
      setSelected(task);
      setSelectedDeps({ blocked_by: [], blocks: [] });
      setSelectedSubs([]);
      void refreshSelected(task.id);
    },
    [refreshSelected],
  );

  useEffect(() => {
    if (!initialTaskId) return;
    let cancelled = false;
    const request = selectedRequest.current;
    api.getTask(initialTaskId).then(info => {
      if (cancelled || selectedRequest.current !== request) return;
      handleSelect(info.task);
    }).catch(e => { if (!cancelled && selectedRequest.current === request) setError(e instanceof Error && /not_found|NOT_FOUND/.test(`${e.message} ${(e as { code?: string }).code ?? ""}`) ? "通知のタスクは削除されたか、表示できません。" : e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [api, initialTaskId, handleSelect]);

  const handleUpdate = useCallback(
    async (id: string, patch: UpdateTaskInput) => {
      const task = tasks.find(item => item.id === id) ?? (selected?.id === id ? selected : null);
      if (task?.execution && patch.status) {
        handleSelect(task);
        return;
      }
      try {
        const r = await api.updateTask(id, patch);
        void queryClient.invalidateQueries({ queryKey: ["task-summary"] });
        if (selectedId.current === id) selectedRequest.current += 1;
        setSelected((prev) => (prev?.id === id ? r.task : prev));
        await refresh();
        await refreshSelected(id);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [api, refresh, refreshSelected, selected, tasks, handleSelect],
  );

  const handleExecutionRecorded = useCallback(async (id: string) => {
    const task = selected?.id === id ? selected : tasks.find(item => item.id === id);
    if (!task) return;
    const result = await api.syncExecution(id, task.workspace_id);
    if (selectedId.current === id) selectedRequest.current += 1;
    setSelected(previous => previous?.id === id ? result.task : previous);
    void queryClient.invalidateQueries({ queryKey: ["task-summary"] });
    await refresh();
  }, [api, selected, tasks, refresh]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await api.deleteTask(id);
        void queryClient.invalidateQueries({ queryKey: ["task-summary"] });
        if (selectedId.current === id) closeSelection();
        await refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [api, refresh, closeSelection],
  );

  const handleCreate = useCallback(
    async (input: CreateTaskInput) => {
      const r = await api.createTask({ ...input, room_id: roomId, project_id: projectId });
      void queryClient.invalidateQueries({ queryKey: ["task-summary"] });
      await refresh();
      handleSelect(r.task);
    },
    [api, roomId, projectId, refresh, handleSelect],
  );

  const handleAddSubtask = useCallback(
    async (parentId: string, title: string) => {
      try {
        await api.createSubtask(parentId, { title });
        await refresh();
        await refreshSelected(parentId);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [api, refresh, refreshSelected],
  );

  const handleSuggestDue = useCallback(
    async (id: string) => {
      try {
        const r = await api.suggestDue(id);
        return { suggested_due_at: r.suggested_due_at, rationale: r.rationale };
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      }
    },
    [api],
  );

  /* ----- Derived filtered collections ------------------------------------ */
  const filteredTasks = useMemo(() => applyFilters(tasks, filters), [tasks, filters]);
  const filteredBoard = useMemo<KanbanView | null>(() => {
    if (!board) return null;
    return {
      columns: board.columns.map<KanbanColumn>((c) => ({
        ...c,
        tasks: applyFilters(c.tasks, filters),
      })),
    };
  }, [board, filters]);
  const filteredGantt = useMemo<GanttView | null>(() => {
    if (!gantt) return null;
    const ids = new Set(applyFilters(gantt.bars.map((b) => b.task), filters).map((t) => t.id));
    return { ...gantt, bars: gantt.bars.filter((b) => ids.has(b.task.id)) };
  }, [gantt, filters]);

  const assignees = useMemo(() => {
    const s = new Set<string>();
    for (const t of tasks) if (t.assignee) s.add(t.assignee);
    return Array.from(s).sort();
  }, [tasks]);

  /* ----- Render ---------------------------------------------------------- */

  return (
    <div className="flex flex-col bg-surface-base text-fg-primary md:min-h-0 md:flex-1 md:overflow-hidden">
      <PageHeader
        view={view}
        onChangeView={setView}
        onRefresh={() => void (async () => { await onRefreshAccess?.(); await refresh(); })()}
        onCreateClick={() => setShowCreate(true)}
        loading={loading}
        totalCount={filteredTasks.length}
        canCreate={canCreate}
      />

      <FilterBar
        members={members}
        filters={filters}
        assignees={assignees}
        onChange={setFilters}
      />

      {error && (
        <div
          role="alert"
          className="mx-6 mt-3 rounded-md border border-danger/40 bg-danger-bg px-3 py-2 text-sm text-danger"
        >
          {error}
        </div>
      )}

      <main className="p-6 md:grow md:overflow-auto">
        {view === "list" &&
          (filteredTasks.length === 0 && !loading ? (
            <EmptyState
              kind={tasks.length === 0 ? "no-tasks" : "filtered"}
              onCreate={canCreate ? () => setShowCreate(true) : undefined}
              onClearFilters={() => setFilters(INITIAL_FILTERS)}
            />
          ) : (
            <TaskList
              members={members}
              tasks={filteredTasks}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onSelect={handleSelect}
              canUpdateTask={canUpdateStatus}
              canDeleteTask={canEdit}
            />
          ))}

        {view === "board" && (
          <TaskBoard
            members={members}
            view={filteredBoard}
            onMove={(id, target) => handleUpdate(id, { status: target })}
            onUpdate={handleUpdate}
            onSelect={handleSelect}
            canMoveTask={canUpdateStatus}
          />
        )}

        {view === "gantt" && (
          <TaskGantt view={filteredGantt} onSelect={handleSelect} />
        )}

        {view === "calendar" &&
          (filteredTasks.length === 0 && !loading ? (
            <EmptyState
              kind={tasks.length === 0 ? "no-tasks" : "filtered"}
              onCreate={canCreate ? () => setShowCreate(true) : undefined}
              onClearFilters={() => setFilters(INITIAL_FILTERS)}
            />
          ) : (
            <TaskCalendar tasks={filteredTasks} onSelect={handleSelect} />
          ))}
      </main>

      <CreateTaskDialog
        members={members}
        open={showCreate && canCreate}
        defaultRoomId={roomId}
        canAssign={canAssign}
        currentUserId={access?.user_id}
        onCancel={() => setShowCreate(false)}
        onSubmit={async (input) => {
          await handleCreate(input);
          setShowCreate(false);
        }}
      />

      <TaskDetailPanel
        commentsApi={api}
        members={members}
        task={selected}
        subtasks={selectedSubs}
        blockedBy={selectedDeps.blocked_by}
        blocks={selectedDeps.blocks}
        onClose={closeSelection}
        onUpdate={handleUpdate}
        onExecutionRecorded={handleExecutionRecorded}
        onDelete={selected && canEdit(selected) ? handleDelete : undefined}
        onAddSubtask={selected && canEdit(selected) ? handleAddSubtask : undefined}
        onSuggestDue={selected && canEdit(selected) ? handleSuggestDue : undefined}
        canEdit={selected ? canEdit(selected) : false}
        canAssign={canAssign}
        canUpdateStatus={selected ? canUpdateStatus(selected) : false}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page header                                                                */
/* -------------------------------------------------------------------------- */

interface PageHeaderProps {
  view: TasksView;
  onChangeView: (v: TasksView) => void;
  onRefresh: () => void;
  onCreateClick: () => void;
  loading: boolean;
  totalCount: number;
  canCreate: boolean;
}

function PageHeader({
  view,
  onChangeView,
  onRefresh,
  onCreateClick,
  loading,
  totalCount,
  canCreate,
}: PageHeaderProps) {
  return (
    <header className="z-10 flex flex-wrap items-center gap-3 border-b border-border-subtle bg-canvas/95 px-6 py-4 backdrop-blur md:sticky md:top-0">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold text-fg-primary">タスク</h1>
        <p className="mt-0.5 text-xs text-fg-muted">
          {totalCount} 件 · リスト / カンバン / ガント / カレンダーで管理
        </p>
      </div>

      <div
        role="tablist"
        aria-label="ビュー切替"
        className="ml-2 inline-flex rounded-md border border-border-default bg-canvas p-0.5"
      >
        {(Object.keys(VIEW_META) as TasksView[]).map((v) => {
          const Icon = VIEW_META[v].icon;
          const active = view === v;
          return (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChangeView(v)}
              className={
                "inline-flex items-center gap-1.5 rounded px-3 py-1 text-sm transition " +
                (active
                  ? "bg-brand-500 text-fg-inverse shadow-sm"
                  : "text-fg-secondary hover:bg-surface-raised hover:text-fg-primary")
              }
            >
              <Icon size={13} aria-hidden />
              {VIEW_META[v].label}
            </button>
          );
        })}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={onRefresh}
          aria-busy={loading}
          aria-label="一覧を更新"
          className={
            "inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-border-default bg-canvas px-3 text-sm text-fg-secondary hover:bg-surface-raised " +
            (loading ? "cursor-progress" : "")
          }
        >
          <RefreshCw
            size={13}
            aria-hidden
            className={loading ? "animate-spin" : ""}
          />
          {loading ? "読込中…" : "更新"}
        </button>
        {canCreate && <button
          type="button"
          onClick={onCreateClick}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-medium text-fg-inverse shadow-sm transition-colors hover:bg-brand-700 active:bg-brand-700 focus-visible:shadow-focus"
        >
          <Plus size={14} aria-hidden />
          新規タスク
        </button>}
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Filter bar                                                                 */
/* -------------------------------------------------------------------------- */

interface FilterBarProps {
  members: { id: string; displayName: string }[];
  filters: Filters;
  assignees: string[];
  onChange: (next: Filters) => void;
}

function FilterBar({ filters, assignees, onChange, members }: FilterBarProps) {
  const pristine =
    filters.statuses.size === 0 &&
    filters.assignee === "" &&
    filters.priority === "all" &&
    filters.due === "any";

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border-subtle bg-canvas px-6 py-3">
      <div className="inline-flex items-center gap-1.5" role="group" aria-label="ステータス">
        {TASK_STATUSES.map((s) => {
          const active = filters.statuses.has(s);
          return (
            <button
              key={s}
              type="button"
              aria-pressed={active}
              onClick={() => {
                const next = new Set(filters.statuses);
                if (next.has(s)) next.delete(s);
                else next.add(s);
                onChange({ ...filters, statuses: next });
              }}
              className={
                "rounded-full px-2.5 py-1 text-xs font-medium transition " +
                (active
                  ? "bg-brand-500 text-fg-inverse ring-1 ring-brand-500"
                  : "bg-canvas text-fg-secondary ring-1 ring-border-subtle hover:bg-surface-raised")
              }
            >
              {STATUS_LABELS_JA[s]}
            </button>
          );
        })}
      </div>

      <span className="hidden h-5 w-px bg-border-subtle sm:inline-block" />

      <Select
        label="優先度"
        value={filters.priority}
        onChange={(v) =>
          onChange({ ...filters, priority: v as TaskPriority | "all" })
        }
      >
        <option value="all">優先度: すべて</option>
        {TASK_PRIORITIES.map((p) => (
          <option key={p} value={p}>
            {PRIORITY_LABELS_JA[p]}
          </option>
        ))}
      </Select>

      <Select
        label="担当者"
        value={filters.assignee}
        onChange={(v) => onChange({ ...filters, assignee: v })}
      >
        <option value="">担当: 全員</option>
        {assignees.map((a) => (
          <option key={a} value={a}>
            @{members.find(member => member.id === a)?.displayName || a}
          </option>
        ))}
      </Select>

      <Select
        label="期限"
        value={filters.due}
        onChange={(v) => onChange({ ...filters, due: v as DueBucket })}
      >
        <option value="any">期限: 全件</option>
        <option value="overdue">期限超過</option>
        <option value="today">今日</option>
        <option value="week">7日以内</option>
        <option value="month">30日以内</option>
        <option value="none">期限なし</option>
      </Select>

      {!pristine && (
        <button
          type="button"
          onClick={() => onChange(INITIAL_FILTERS)}
          className="ml-auto rounded-md border border-border-default bg-canvas px-3 py-1.5 text-xs text-fg-secondary hover:bg-surface-raised"
        >
          フィルタを解除
        </button>
      )}
    </div>
  );
}

interface SelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}

function Select({ label, value, onChange, children }: SelectProps) {
  return (
    <label className="inline-flex items-center gap-1 text-xs text-fg-muted">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-md border border-border-default bg-canvas px-2 text-sm text-fg-secondary focus-visible:shadow-focus"
      >
        {children}
      </select>
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Empty states                                                               */
/* -------------------------------------------------------------------------- */

function EmptyState({
  kind,
  onCreate,
  onClearFilters,
}: {
  kind: "no-tasks" | "filtered";
  onCreate?: () => void;
  onClearFilters: () => void;
}) {
  if (kind === "filtered") {
    return (
      <div className="mx-auto mt-12 flex max-w-sm flex-col items-center gap-3 text-center">
        <CheckSquare size={28} aria-hidden className="text-fg-subtle" />
        <p className="text-sm font-medium text-fg-primary">
          条件に一致するタスクがありません
        </p>
        <p className="text-xs text-fg-muted">
          {onCreate ? "フィルタを調整するか、新しいタスクを作成してください。" : "フィルタを調整してください。"}
        </p>
        <button
          type="button"
          onClick={onClearFilters}
          className="rounded-md border border-border-default bg-canvas px-3 py-1.5 text-xs text-fg-secondary hover:bg-surface-raised"
        >
          フィルタを解除
        </button>
      </div>
    );
  }
  return (
    <div className="mx-auto mt-12 flex max-w-md flex-col items-center gap-3 rounded-lg border border-dashed border-border-default bg-canvas py-12 text-center">
      <ListChecks size={32} aria-hidden className="text-fg-subtle" />
      <p className="text-sm font-medium text-fg-primary">
        まだタスクはありません
      </p>
      <p className="px-6 text-xs text-fg-muted">
        {onCreate ? "メッセージから「タスク化」したり、「+ 新規タスク」から作成できます。" : "割り当てられたタスクがここに表示されます。"}
      </p>
      {onCreate && <button
        type="button"
        onClick={onCreate}
        className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-fg-inverse hover:bg-brand-700"
      >
        <Plus size={13} aria-hidden />
        最初のタスクを作成
      </button>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Filter logic                                                               */
/* -------------------------------------------------------------------------- */

function applyFilters(tasks: Task[], filters: Filters): Task[] {
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  return tasks.filter((t) => {
    if (filters.statuses.size > 0 && !filters.statuses.has(t.status)) return false;
    if (filters.priority !== "all" && t.priority !== filters.priority) return false;
    if (filters.assignee && t.assignee !== filters.assignee) return false;
    switch (filters.due) {
      case "any":
        break;
      case "overdue":
        if (t.due_at === null || t.due_at >= now || t.status === "done") return false;
        break;
      case "today": {
        if (t.due_at === null) return false;
        const d = new Date(t.due_at);
        const today = new Date();
        if (
          d.getFullYear() !== today.getFullYear() ||
          d.getMonth() !== today.getMonth() ||
          d.getDate() !== today.getDate()
        ) {
          return false;
        }
        break;
      }
      case "week":
        // include overdue + due within 7 days
        if (t.due_at === null) return false;
        if (t.due_at > now + 7 * day) return false;
        break;
      case "month":
        if (t.due_at === null) return false;
        if (t.due_at > now + 30 * day) return false;
        break;
      case "none":
        if (t.due_at !== null) return false;
        break;
    }
    return true;
  });
}

/* -------------------------------------------------------------------------- */
/* Create-task dialog                                                         */
/* -------------------------------------------------------------------------- */

interface CreateTaskDialogProps {
  members: { id: string; displayName: string }[];
  open: boolean;
  defaultRoomId?: string;
  onSubmit: (input: CreateTaskInput) => Promise<void>;
  onCancel: () => void;
  canAssign: boolean;
  currentUserId?: string;
}

function CreateTaskDialog({
  members,
  open,
  defaultRoomId,
  onSubmit,
  onCancel,
  canAssign,
  currentUserId,
}: CreateTaskDialogProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("P2");
  const [status, setStatus] = useState<TaskStatus>("todo");
  const [dueAt, setDueAt] = useState("");
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset form whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setPriority("P2");
      setStatus("todo");
      setDueAt("");
      setAssignee("");
      setErr(null);
      setBusy(false);
    }
  }, [open]);

  // Esc to close (when open).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const dueMs = (() => {
    if (!dueAt) return null;
    const d = new Date(dueAt + "T00:00:00");
    return isNaN(d.getTime()) ? null : d.getTime();
  })();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-task-title"
      className="fixed inset-0 z-40 flex items-start justify-center bg-fg-primary/30 p-4 backdrop-blur-sm sm:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <form
        data-testid="create-task-dialog"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!title.trim()) {
            setErr("タイトルを入力してください");
            return;
          }
          setBusy(true);
          setErr(null);
          try {
            await onSubmit({
              title: title.trim(),
              description: description.trim() || undefined,
              priority,
              status,
              assignee: canAssign ? assignee.trim() || null : currentUserId,
              due_at: dueMs,
              room_id: defaultRoomId ?? null,
            });
          } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
          } finally {
            setBusy(false);
          }
        }}
        className="flex w-full max-w-lg flex-col gap-3 rounded-lg bg-canvas p-5 shadow-lg ring-1 ring-border-subtle"
      >
        <header className="flex items-start justify-between">
          <div>
            <h2 id="create-task-title" className="text-md font-semibold text-fg-primary">
              新しいタスク
            </h2>
            <p className="text-xs text-fg-muted">
              {canAssign ? "ステータス・期限・担当者は後から変更できます。" : "自分のタスクを作成します。"}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="閉じる"
            className="rounded-md border border-border-default bg-canvas px-2 py-1 text-xs text-fg-secondary hover:bg-surface-raised"
          >
            ✕
          </button>
        </header>

        <DialogField label="タイトル" required>
          <input
            autoFocus
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="例: 5月のリリース計画書を作成"
            className="h-10 w-full rounded-md border border-border-default bg-canvas px-3 text-sm focus-visible:shadow-focus"
          />
        </DialogField>

        <DialogField label="詳細 (Markdown 可)">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="背景・前提・受け入れ条件など"
            className="w-full rounded-md border border-border-default bg-canvas px-3 py-2 text-sm focus-visible:shadow-focus"
          />
        </DialogField>

        <div className="grid grid-cols-2 gap-3">
          <DialogField label="ステータス">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as TaskStatus)}
              className="h-10 w-full rounded-md border border-border-default bg-canvas px-3 text-sm focus-visible:shadow-focus"
            >
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS_JA[s]}
                </option>
              ))}
            </select>
          </DialogField>
          <DialogField label="優先度">
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
              className="h-10 w-full rounded-md border border-border-default bg-canvas px-3 text-sm focus-visible:shadow-focus"
            >
              {TASK_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS_JA[p]}
                </option>
              ))}
            </select>
          </DialogField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <DialogField label="期限" helperText="任意">
            <input
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="h-10 w-full rounded-md border border-border-default bg-canvas px-3 text-sm focus-visible:shadow-focus"
            />
          </DialogField>
          <DialogField label="担当者" helperText={canAssign ? "任意" : "担当者の変更はタスク管理者が行います。"}>
            <select disabled={!canAssign} value={canAssign ? assignee : currentUserId || ""} onChange={e => setAssignee(e.target.value)} className="h-10 w-full rounded-md border border-border-default bg-canvas px-3 text-sm focus-visible:shadow-focus disabled:opacity-60"><option value="">未割り当て</option>{members.map(member => <option key={member.id} value={member.id}>{member.displayName}</option>)}</select>
          </DialogField>
        </div>

        {err && (
          <p role="alert" className="text-xs text-danger">
            {err}
          </p>
        )}

        <div className="mt-1 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border-default bg-canvas px-3 py-1.5 text-sm text-fg-secondary hover:bg-surface-raised"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-fg-inverse hover:bg-brand-700 disabled:opacity-50"
          >
            {busy ? "作成中…" : "タスクを作成"}
          </button>
        </div>
      </form>
    </div>
  );
}

function DialogField({
  label,
  required,
  helperText,
  children,
}: {
  label: string;
  required?: boolean;
  helperText?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-fg-secondary">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </span>
      {children}
      {helperText && (
        <span className="text-[11px] text-fg-muted">{helperText}</span>
      )}
    </label>
  );
}
